import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { CreateUnitDto } from '../types';
import UnitForm from '../components/forms/UnitForm';
import { ArrowLeft } from 'lucide-react';

const CreateUnitPage: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const createUnitMutation = useMutation({
		mutationFn: async (data: CreateUnitDto) => {
			const result = await apiService.createUnit(data);
			return result;
		},
		onSuccess: (response) => {
			if (response.success && response.data) {
				queryClient.invalidateQueries({ queryKey: ['units'] });
				toast.success(`Unit "${response.data.unitNumber}" created successfully!`);
				navigate(`/units/${response.data.unitId}`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error creating unit:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error creating unit: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: CreateUnitDto) => {
		createUnitMutation.mutate(data);
	};

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="mb-2">
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Unit</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add a new rental unit</p>
				</div>
				{/* Back to Units Link */}
				<button
					onClick={() => navigate('/units')}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Units</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<UnitForm
					onSubmit={handleSubmit}
					isLoading={createUnitMutation.isPending}
					error={createUnitMutation.error ? 'Failed to create unit' : undefined}
				/>
			</div>
		</div>
	);
};

export default CreateUnitPage;

